import type { GroupSelection } from './groupFilter';

/**
 * Group-aware payload builders for the two "apply to all" bulk actions on
 * the opener tab. Both keep the no-filter ("All groups") behavior byte-
 * identical to the pre-group payloads — that path is regression-critical.
 */

/** Backend caps `pairIds` at 100 per /pairs/bulk-opener-enabled request. */
export const BULK_ENABLE_PAIR_IDS_CHUNK = 100;
/** Backend caps `ruleIds` at 500 per /rules/bulk-params request. */
export const BULK_PARAMS_RULE_IDS_CHUNK = 500;

export function chunkIds(ids: string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

export interface BulkEnablePayload {
  scope: 'all' | 'ids';
  enabled: boolean;
  pairIds?: string[];
  groupId?: string;
}

/**
 * "All ON / All OFF" payload(s). A selected group narrows the server-side
 * scope:'all' via `groupId`; "Ungrouped" has NO server-side narrowing, so
 * it reuses the scope:'ids' path over the currently-visible (ungrouped)
 * pair ids, chunked at the backend cap.
 */
export function buildBulkEnableAllPayloads(
  selection: GroupSelection,
  enabled: boolean,
  visiblePairIds: string[]
): BulkEnablePayload[] {
  if (selection.kind === 'all') return [{ scope: 'all', enabled }];
  if (selection.kind === 'group') {
    return [{ scope: 'all', enabled, groupId: selection.groupId }];
  }
  return chunkIds(visiblePairIds, BULK_ENABLE_PAIR_IDS_CHUNK).map(pairIds => ({
    scope: 'ids',
    pairIds,
    enabled
  }));
}

/**
 * Honest pair total for the "All ON / All OFF" confirm dialog. "All groups"
 * and a real group flip server-side (`scope:'all'` [+ `groupId`]) — the
 * WHOLE group-scoped universe, not just the search-narrowed visible rows —
 * so the dialog must quote the UNSEARCHED group-scoped pair count.
 * "Ungrouped" rides the scope:'ids' path over the visible rows, so there
 * the visible selectable count is the accurate one.
 */
export function bulkAllConfirmCount(
  selection: GroupSelection,
  groupPairCount: number,
  visibleSelectableCount: number
): number {
  return selection.kind === 'ungrouped'
    ? visibleSelectableCount
    : groupPairCount;
}

export interface BulkParamsValues {
  thresholdSum?: number;
  size?: number;
  maxVolumeContracts?: number;
  /**
   * Maker size mode — dynamic sizes the maker to the counter top-of-book,
   * fixed rests the rule's full Size. Callers set the key ONLY when the
   * operator picked a mode (the bar's "—" leaves it out entirely), so the
   * no-mode payload stays byte-identical to the pre-mode one.
   */
  makerSizeMode?: 'dynamic' | 'fixed';
  /**
   * Counter-stuck market-hedge timeout (seconds). Callers set the key ONLY
   * when the operator filled the field (blank leaves it out entirely), same
   * contract as `makerSizeMode` — the no-value payload stays byte-identical.
   */
  counterStuckMarketHedgeSeconds?: number;
  counterMakerWalkStepSeconds?: number | null;
  counterMakerWalkStepCents?: number | null;
  lockstepMaxAheadBatches?: number | null;
}

export interface BulkParamsDto extends BulkParamsValues {
  scope: 'all' | 'ids';
  ruleIds?: string[];
  path: 'a' | 'b';
}

/**
 * Set-all threshold/size/maxV DTO(s) — a RULES op. "All groups" keeps the
 * single scope:'all' request; a group / Ungrouped filter resolves to the
 * PRE-SEARCH group-scoped pairs' rule ids (the whole group, not just the
 * search-narrowed visible rows — consistent with the group "All ON/OFF")
 * and sends scope:'ids', chunked defensively at the backend's 500-id cap.
 * An empty id list yields NO requests (nothing to update — the caller
 * still stages the pre-rule seed).
 */
export function buildBulkParamsDtos(
  selection: GroupSelection,
  path: 'a' | 'b',
  values: BulkParamsValues,
  scopedRuleIds: string[]
): BulkParamsDto[] {
  if (selection.kind === 'all') return [{ scope: 'all', path, ...values }];
  return chunkIds(scopedRuleIds, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    path,
    ...values
  }));
}

/**
 * Applies each bulk-params DTO sequentially and sums the per-chunk
 * `modified` counters into the single number the bar reports ("Updated N
 * rules"). Sequential on purpose — mirrors the other chunked bulk loops so
 * a mid-loop failure leaves a clean prefix applied.
 */
export async function applyBulkParamsDtos(
  dtos: BulkParamsDto[],
  apply: (dto: BulkParamsDto) => Promise<{ modified: number }>
): Promise<number> {
  let totalModified = 0;
  for (const dto of dtos) {
    totalModified += (await apply(dto)).modified;
  }
  return totalModified;
}
